import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../utils/apiClient';
import AlertFrequencySelector, { AlertFrequency } from './AlertFrequencySelector';

interface SimilarJurisprudence {
  jurisprudenceId: string;
  similarityScore: number;
  jurisdiction: string;
  chamber: string;
  number: string;
  decision_date: string;
  solution: string;
  title: string;
}

interface SimilarityData {
  jurisprudenceId: string;
  totalFound: number;
  limit: number;
  similarities: SimilarJurisprudence[];
  suivi: {
    similar_cases_count: number;
    similarity_tracking: {
      initial_count: number;
      current_count: number;
      last_check: string;
    };
  };
}

interface FollowedJurisprudence {
  _id: string;
  userId: string;
  jurisprudenceId: string;
  nombreRapprochements: number;
  dateAjout: string;
  alertFrequency: string;
  isActive: boolean;
  lastAlertSent: string | null;

  id: string;
  jurisdiction: string;
  chamber: string;
  number: string;
  solution: string;
  themes: string[];
  decision_date: string;
  title: string;
  hasMetadata: boolean;
  primary_issues: string[];
  issue_taxonomy: any;
  metadata_confidence: number | null;
  sub_domains: string[];
  primary_domain: string;
  similar_cases_count: number;

  similarity_tracking?: {
    initial_count: number;
    current_count: number;
    has_changed: boolean;
    change_amount: number;
    change_direction: 'increase' | 'decrease' | 'stable';
    last_check: string;
    is_fresh: boolean;
  };
}

const JurisprudenceFollow = () => {
  const [suivis, setSuivis] = useState<FollowedJurisprudence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [loadingSimilarities, setLoadingSimilarities] = useState<Set<string>>(new Set());
  const [similaritiesData, setSimilaritiesData] = useState<Map<string, SimilarityData>>(new Map());
  const [expandedSuivis, setExpandedSuivis] = useState<Set<string>>(new Set());

  const navigate = useNavigate();

  const fetchSuivis = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient('/jurisprudence-suivi/');

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error('Erreur lors de la récupération des suivis');
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setSuivis(data);
      } else if (data.suivis && Array.isArray(data.suivis)) {
        setSuivis(data.suivis);
      } else {
        console.warn('Structure de réponse inattendue pour /jurisprudence-suivi/:', data);
        throw new Error('Structure de réponse inattendue du serveur');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des suivis:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Impossible de récupérer les suivis. Veuillez réessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuivis();
  }, []);

  const fetchSimilarities = async (jurisprudenceId: string, limit: number = 999999999) => {
    if (loadingSimilarities.has(jurisprudenceId)) return;

    try {
      setLoadingSimilarities(prev => new Set(prev).add(jurisprudenceId));

      const response = await apiClient(
        `/jurisprudence-suivi/${jurisprudenceId}/similarities?limit=${limit}`
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des similarités');
      }

      const similarityData: SimilarityData = await response.json();

      setSimilaritiesData(prev => {
        const newMap = new Map(prev);
        newMap.set(jurisprudenceId, similarityData);
        return newMap;
      });

      setExpandedSuivis(prev => new Set(prev).add(jurisprudenceId));
    } catch (err) {
      console.error(`Erreur chargement similarités ${jurisprudenceId}:`, err);
      setError(
        `Erreur lors du chargement des similarités: ${err instanceof Error ? err.message : 'Erreur inconnue'}`
      );
    } finally {
      setLoadingSimilarities(prev => {
        const newSet = new Set(prev);
        newSet.delete(jurisprudenceId);
        return newSet;
      });
    }
  };

  const handleUnfollow = async (jurisprudenceId: string) => {
    try {
      const response = await apiClient(`/jurisprudence-suivi/${jurisprudenceId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la suppression du suivi');
      }

      setSuivis(currentSuivis =>
        currentSuivis.filter(suivi => suivi.jurisprudenceId !== jurisprudenceId)
      );

      setSimilaritiesData(prev => {
        const newMap = new Map(prev);
        newMap.delete(jurisprudenceId);
        return newMap;
      });

      setExpandedSuivis(prev => {
        const newSet = new Set(prev);
        newSet.delete(jurisprudenceId);
        return newSet;
      });
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression du suivi');
    }
  };

  const toggleExpanded = (jurisprudenceId: string) => {
    setExpandedSuivis(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jurisprudenceId)) {
        newSet.delete(jurisprudenceId);
      } else {
        if (!similaritiesData.has(jurisprudenceId)) {
          fetchSimilarities(jurisprudenceId, 999999999);
        } else {
          newSet.add(jurisprudenceId);
        }
      }
      return newSet;
    });
  };

  const formatChangeDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return 'Date inconnue';
    }
  };

  const formatSimilarityScore = (score: number) => {
    return `${Math.round(score * 100)}%`;
  };

  const formatAlertFrequency = (frequency: string) => {
    switch (frequency.toLowerCase()) {
      case 'immediate':
        return 'Alerte immédiate';
      case 'daily':
        return 'Récap quotidien';
      case 'weekly':
        return 'Récap hebdomadaire';
      default:
        return frequency;
    }
  };

  const formatLastAlertSent = (date: string | null) => {
    if (!date) return 'Aucune alerte envoyée à ce jour';
    try {
      const sent = new Date(date);
      const diffMs = Date.now() - sent.getTime();
      const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
      if (diffDays === 0) return "Dernière alerte : aujourd'hui";
      if (diffDays === 1) return 'Dernière alerte : hier';
      return `Dernière alerte : il y a ${diffDays} jours`;
    } catch {
      return null;
    }
  };

  const handleFrequencyChange = (jurisprudenceId: string, next: AlertFrequency) => {
    setSuivis(prev =>
      prev.map(s => (s.jurisprudenceId === jurisprudenceId ? { ...s, alertFrequency: next } : s))
    );
  };

  const getSolutionColor = (solution: string) => {
    switch (solution.toLowerCase()) {
      case 'rejet':
        return 'text-red-600 dark:text-red-400';
      case 'cassation':
        return 'text-orange-600 dark:text-orange-400';
      case 'admission':
        return 'text-green-600 dark:text-green-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</p>
          <button
            onClick={fetchSuivis}
            className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="bg-mcdm-primary-100 dark:bg-mcdm-third-300 p-4 rounded-lg animate-pulse"
              >
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-full mb-2"></div>
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {suivis.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {suivis.map(suivi => {
                  const isExpanded = expandedSuivis.has(suivi.jurisprudenceId);
                  const isLoadingSim = loadingSimilarities.has(suivi.jurisprudenceId);
                  const similarityData = similaritiesData.get(suivi.jurisprudenceId);
                  const tracking = suivi.similarity_tracking;

                  return (
                    <div
                      key={suivi._id}
                      className="bg-mcdm-primary-100 dark:bg-mcdm-third-300 rounded-lg border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="p-4 pb-2">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-xl font-platypi font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 leading-snug">
                              {new Date(suivi.decision_date).toLocaleDateString('fr-FR')} -{' '}
                              {suivi.jurisdiction}
                              {suivi.chamber ? ` - ${suivi.chamber}` : ''}
                              {suivi.number ? ` n° ${suivi.number}` : ''}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              Jurisprudence suivie depuis le{' '}
                              {new Date(suivi.dateAjout).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <button
                            onClick={() => handleUnfollow(suivi.jurisprudenceId)}
                            className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
                                                         bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300
                                                         hover:bg-red-200 dark:hover:bg-red-800 transition-colors duration-200
                                                         flex-shrink-0 ml-3"
                          >
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                            Retirer
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3 py-2 border-y border-gray-200 dark:border-gray-600">
                          <div className="text-center">
                            <div className="text-xl font-bold text-mcdm-third-500 dark:text-mcdm-primary-100">
                              {suivi.similar_cases_count}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              similaire{suivi.similar_cases_count > 1 ? 's' : ''}
                            </div>
                          </div>

                          <div className="text-center">
                            {tracking && tracking.has_changed ? (
                              <>
                                <div
                                  className={`text-xl font-bold ${
                                    tracking.change_direction === 'increase'
                                      ? 'text-green-600 dark:text-green-400'
                                      : tracking.change_direction === 'decrease'
                                        ? 'text-red-600 dark:text-red-400'
                                        : 'text-gray-600 dark:text-gray-400'
                                  }`}
                                >
                                  {tracking.change_direction === 'increase' && '+'}
                                  {tracking.change_amount}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  depuis le {formatChangeDate(tracking.last_check)}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-lg text-gray-500 dark:text-gray-400">—</div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  Vérifié le{' '}
                                  {formatChangeDate(tracking?.last_check || suivi.dateAjout)}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {suivi.primary_issues && suivi.primary_issues.length > 0 && (
                        <div className="px-4 pb-3">
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                              Questions principales
                            </h5>
                            <ul className="space-y-1">
                              {suivi.primary_issues.slice(0, 3).map((question, qIndex) => (
                                <li
                                  key={qIndex}
                                  className="text-sm text-mcdm-third-500 dark:text-gray-400 flex items-start"
                                >
                                  <span className="w-1.5 h-1.5 bg-mcdm-third-400 dark:bg-mcdm-primary-100 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                  {question}
                                </li>
                              ))}
                              {suivi.primary_issues.length > 3 && (
                                <li className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200">
                                  +{suivi.primary_issues.length - 3} autres questions...
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}

                      <div className="px-4 pb-3">
                        {!similarityData ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => fetchSimilarities(suivi.jurisprudenceId)}
                              disabled={isLoadingSim || suivi.similar_cases_count === 0}
                              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded transition-colors duration-200 ${
                                suivi.similar_cases_count === 0
                                  ? 'bg-mcdm-green-500 text-mcdm-primary-100 dark:bg-mcdm-green-500 dark:text-mcdm-primary-100 cursor-not-allowed hover:bg-mcdm-green-500 dark:hover:bg-mcdm-green-500'
                                  : isLoadingSim
                                    ? 'bg-mcdm-green-400 text-mcdm-primary-100 dark:bg-mcdm-green-400 dark:text-mcdm-primary-100 opacity-50 cursor-not-allowed'
                                    : 'bg-mcdm-green-400 text-mcdm-primary-100 dark:bg-mcdm-green-400 dark:text-mcdm-primary-100 hover:bg-mcdm-green-500 dark:hover:bg-mcdm-green-500'
                              }`}
                            >
                              {isLoadingSim ? (
                                <>
                                  <svg
                                    className="w-4 h-4 animate-spin"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                  </svg>
                                  Chargement...
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                  </svg>
                                  {suivi.similar_cases_count === 0
                                    ? 'Aucune similarité'
                                    : suivi.similar_cases_count === 1
                                      ? 'Voir la similarité'
                                      : `Voir les ${suivi.similar_cases_count} similarités`}
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => {}}
                              className="flex-1 px-3 py-2 text-sm font-medium
                                                             bg-mcdm-green-100 text-white dark:bg-mcdm-third-100 dark:text-white
                                                             hover:bg-green-600 dark:hover:bg-mcdm-third-200
                                                             rounded transition-colors duration-200 flex items-center justify-center gap-2"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              Résumé
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <h4 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                                Jurisprudences similaires :
                              </h4>
                              <button
                                onClick={() => {}}
                                className="px-2 py-1 text-xs font-medium
                                                                 bg-mcdm-green-100 text-white dark:bg-mcdm-green-100 dark:text-white
                                                                 hover:bg-mcdm-green-500 dark:hover:bg-mcdm-green-500
                                                                 rounded transition-colors duration-200 flex items-center gap-1"
                              >
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                                Résumé
                              </button>
                            </div>

                            <div className="space-y-1">
                              {(isExpanded
                                ? similarityData.similarities
                                : similarityData.similarities.slice(0, 3)
                              ).map((similarite, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-2 p-2 bg-white dark:bg-mcdm-third-400 rounded text-xs border border-gray-100 dark:border-gray-600"
                                >
                                  <span className="w-1 h-1 bg-mcdm-green-500 rounded-full flex-shrink-0"></span>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-800 dark:text-gray-200 truncate">
                                      {similarite.jurisdiction},{' '}
                                      {new Date(similarite.decision_date).toLocaleDateString(
                                        'fr-FR'
                                      )}
                                      , n° {similarite.number}
                                    </div>
                                    <div className="text-gray-600 dark:text-gray-400 truncate">
                                      {similarite.title}
                                    </div>
                                  </div>
                                  <span className="text-xs font-medium px-1.5 py-0.5 bg-mcdm-green-100 text-mcdm-green-500 dark:bg-blue-900 dark:text-blue-300 rounded flex-shrink-0">
                                    {formatSimilarityScore(similarite.similarityScore)}
                                  </span>
                                </div>
                              ))}
                            </div>

                            {similarityData.similarities.length > 3 && (
                              <div className="text-center pt-1">
                                <button
                                  onClick={() => toggleExpanded(suivi.jurisprudenceId)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-mcdm-secondary-500 hover:text-mcdm-secondary-600 dark:text-mcdm-secondary-400 dark:hover:text-mcdm-secondary-300 font-medium transition-colors"
                                >
                                  {isExpanded ? (
                                    <>
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M5 15l7-7 7 7"
                                        />
                                      </svg>
                                      Masquer
                                    </>
                                  ) : (
                                    <>
                                      Liste complète ({similarityData.totalFound})
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 9l-7 7-7-7"
                                        />
                                      </svg>
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-mcdm-third-400 rounded-b-lg">
                        <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400 flex-wrap gap-2">
                          <div className="flex items-center gap-3 flex-wrap">
                            {suivi.isActive ? (
                              <AlertFrequencySelector
                                jurisprudenceId={suivi.jurisprudenceId}
                                value={(suivi.alertFrequency as AlertFrequency) ?? 'daily'}
                                onChange={next =>
                                  handleFrequencyChange(suivi.jurisprudenceId, next)
                                }
                              />
                            ) : (
                              <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                Suivi inactif
                              </span>
                            )}
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatLastAlertSent(suivi.lastAlertSent)}
                            </span>
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                navigate(`/jurisprudences/alertes?suiviId=${suivi._id}`);
                              }}
                              className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-100 hover:underline"
                            >
                              Voir l'historique des alertes →
                            </button>
                          </div>
                          <span className={`font-medium ${getSolutionColor(suivi.solution)}`}>
                            {suivi.solution}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="mx-auto w-24 h-24 mb-4">
                  <svg
                    className="w-full h-full text-gray-300 dark:text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                </div>
                <p className="text-mcdm-third-500 dark:text-mcdm-primary-200 text-lg font-medium mb-2">
                  Vous ne suivez aucune jurisprudence pour le moment.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Utilisez l'onglet "Recherche" pour découvrir des jurisprudences et les suivre.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default JurisprudenceFollow;
